import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { User, RefreshCw, Image, CalendarDays, Building2, Briefcase, FlaskConical, LayoutDashboard } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { useAuthStore } from "@/stores/authStore"
import { suppressOAuthAutoStart } from '@/lib/oauthAutoStart'
import { useAuthMe } from "@/hooks/useAuthMe"
import { useConfig } from "@/hooks/useConfig"
import { enterStaging, exitStaging } from "@/lib/staging"
import { API_BASE_URL } from "@/lib/constants"

export function UserMenu() {
  const { t } = useTranslation()
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const { data: authMe } = useAuthMe()
  const { data: config } = useConfig()
  // 권위 있는 출처는 /auth/me (AdminRoute와 동일). persist된 user.isAdmin은 오래된
  // 로그인 blob에서 누락될 수 있어 authMe를 우선 쓰고, 로딩 중에는 user로 폴백한다.
  const isAdmin = (authMe?.isAdmin ?? user?.isAdmin) === true
  const isStaging = config?.isStaging === true
  // gov(NCloud) 배포는 스테이징 카운터파트가 없으므로 전환 메뉴를 숨긴다.
  // stagingAvailable이 명시적으로 false일 때만 숨김 (미적용/로딩 시 기존 동작 유지).
  const stagingAvailable = config?.stagingAvailable !== false
  const canEnterStaging = !isStaging && isAdmin && stagingAvailable

  const handleLogout = () => {
    // 명시적 로그아웃: 랜딩 시 OAuth 자동 시도로 조용히 다시 로그인되지 않도록 이 세션에서 중단
    suppressOAuthAutoStart()
    logout()
    setLogoutDialogOpen(false)
  }

  const handleRefreshToken = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (response.ok) {
        toast.success(t('userMenu.tokenRefreshSuccess'))
      } else {
        const errorText = await response.text()
        toast.error(t('userMenu.tokenRefreshFailed'), {
          description: `${response.status}: ${errorText}`,
        })
      }
    } catch (error) {
      toast.error(t('userMenu.tokenRefreshFailed'), {
        description: error instanceof Error ? error.message : t('userMenu.networkError'),
      })
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2">
            <User className="w-[18px] h-[18px]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} className="min-w-48 p-2 rounded-xl shadow-lg border-slate-200">
          {/* User Info Section */}
          <div className="px-3 py-3 mb-1">
            <div className="flex items-center gap-3">
              {authMe?.source === 'gov' ? (
                <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                  <Building2 className="w-4 h-4 text-blue-600" />
                </div>
              ) : authMe?.source === 'wrks' ? (
                <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                  <Briefcase className="w-4 h-4 text-emerald-600" />
                </div>
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-slate-600" />
                </div>
              )}
              <p className="text-sm font-medium text-slate-700">{user?.email}</p>
            </div>
          </div>
          {/* Stats Section */}
          {authMe?.stats && (
            <>
              <DropdownMenuSeparator className="bg-slate-100" />
              <div className="px-3 py-2.5">
                <div className="flex items-center justify-between text-sm text-slate-600 mb-1.5">
                  <div className="flex items-center gap-2">
                    <Image className="w-4 h-4 text-slate-400" />
                    <span>{t('userMenu.totalImages')}</span>
                  </div>
                  <span className="ml-4 font-semibold text-slate-800">{t('userMenu.imageCount', { count: authMe.stats.totalImages })}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-slate-400" />
                    <span>{t('userMenu.thisMonthImages')}</span>
                  </div>
                  <span className="ml-4 font-semibold text-slate-800">{t('userMenu.imageCount', { count: authMe.stats.thisMonthImages })}</span>
                </div>
              </div>
            </>
          )}
          <DropdownMenuSeparator className="bg-slate-100" />
          {isAdmin && (
            <DropdownMenuItem
              onClick={() => navigate('/admin')}
              className="mt-1 px-3 py-2.5 rounded-lg text-slate-600 hover:text-slate-800 focus:text-slate-800 hover:bg-slate-100 focus:bg-slate-100 cursor-pointer transition-colors"
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              <span className="font-medium">{t('userMenu.goToAdmin')}</span>
            </DropdownMenuItem>
          )}
          {canEnterStaging && (
            <DropdownMenuItem
              onClick={() => enterStaging()}
              className="mt-1 px-3 py-2.5 rounded-lg text-amber-700 hover:text-amber-800 focus:text-amber-800 hover:bg-amber-50 focus:bg-amber-50 cursor-pointer transition-colors"
            >
              <FlaskConical className="w-4 h-4 mr-2" />
              <span className="font-medium">{t('userMenu.switchToStaging')}</span>
            </DropdownMenuItem>
          )}
          {isStaging && (
            <DropdownMenuItem
              onClick={() => exitStaging()}
              className="mt-1 px-3 py-2.5 rounded-lg text-amber-700 hover:text-amber-800 focus:text-amber-800 hover:bg-amber-50 focus:bg-amber-50 cursor-pointer transition-colors"
            >
              <FlaskConical className="w-4 h-4 mr-2" />
              <span className="font-medium">{t('userMenu.exitToProduction')}</span>
            </DropdownMenuItem>
          )}
          {import.meta.env.DEV && (
            <DropdownMenuItem
              onClick={handleRefreshToken}
              className="mt-1 px-3 py-2.5 rounded-lg text-slate-600 hover:text-slate-800 hover:bg-slate-100 cursor-pointer transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              <span className="font-medium">{t('userMenu.refreshToken')}</span>
              <span className="ml-auto text-xs text-slate-400">DEV</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => setLogoutDialogOpen(true)}
            className="mt-1 px-3 py-2.5 rounded-lg text-slate-600 hover:text-red-600 focus:text-red-600 hover:bg-red-50 focus:bg-red-50 cursor-pointer transition-colors justify-end"
          >
            <span className="font-medium">{t('auth.logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent className="sm:max-w-[360px]">
          <DialogHeader className="space-y-3">
            <DialogTitle>{t('auth.logout')}</DialogTitle>
            <DialogDescription>
              {t('auth.logoutConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setLogoutDialogOpen(false)}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="default"
              onClick={handleLogout}
              className="flex-1"
            >
              {t('auth.logout')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
